import React, { useState } from "react";
import { Box, Button, Text, HStack, Flex } from "@chakra-ui/react";

const PREVIEW_CHAR_LIMIT = 120;

// Map capability IDs to short user-facing labels
const getCapabilityLabel = (capabilityId) => {
  const labels = {
    update_goal_statement:               "Update possible solution",
    update_problem_statement:            "Update problem statement",
    update_target_statement:             "Update target audience",
    update_summary:                      "Update project description",
    update_design_challenge:             "Update design challenge",
    update_objectives:                   "Update objectives",
    update_deliverables:                 "Update deliverables",
    update_metrics:                      "Update metrics",
    update_stakeholders:                 "Update stakeholders",
    update_experience_design_principles: "Update design principles",
    update_design_vision:                "Update design vision",
    update_strategic_themes:             "Update strategic themes",
    add_tools_to_stages:                 "Add tasks to stage",
    remove_tools_from_stages:            "Remove tasks",
    modify_tools_content:                "Modify task content",
    tool_duration_editing:               "Update task duration",
    bulk_actions:                        "Apply multiple changes",
    enable_feature:                      "Enable feature",
  };
  return labels[capabilityId] || capabilityId?.replace(/_/g, " ") || "Apply change";
};

// Build a plain-text description for a single sub-action preview object.
// Prefers an explicit description (the real modification content) over a
// synthesized summary so truncation targets meaningful text, not field labels.
const getSubActionDescription = (subAction) => {
  if (!subAction) return "";

  // Prefer agent-provided doneLabel (native tool calling — agent self-describes)
  if (subAction.doneLabel && typeof subAction.doneLabel === "string") {
    return subAction.doneLabel;
  }

  // Prefer an explicit description field (real content, not synthesized)
  if (subAction.description && typeof subAction.description === "string") {
    return subAction.description;
  }

  const type = subAction.type || "";
  const tool = subAction.tool || "";
  const stage = subAction.stage || "";
  const program = subAction.program || "";
  const tools = Array.isArray(subAction.tools) ? subAction.tools.join(", ") : "";
  const mods = subAction.modifications || "";
  const dur = subAction.newDuration != null ? `${subAction.newDuration}h` : "";

  if (type === "Duration Change" || type === "tool_duration_editing") {
    const parts = ["Duration change"];
    if (tool) parts.push(`"${tool}"`);
    if (dur) parts.push(`→ ${dur}`);
    if (stage) parts.push(`in ${stage}`);
    if (program) parts.push(`(${program})`);
    return parts.join(" ");
  }
  if (type === "Add Tools" || type === "add_tools_to_stages") {
    const parts = ["Add"];
    if (tools) parts.push(tools);
    else if (tool) parts.push(`"${tool}"`);
    if (stage) parts.push(`to ${stage}`);
    if (program) parts.push(`(${program})`);
    return parts.join(" ");
  }
  if (type === "Remove Tools" || type === "remove_tools_from_stages") {
    const parts = ["Remove"];
    if (tools) parts.push(tools);
    else if (tool) parts.push(`"${tool}"`);
    if (stage) parts.push(`from ${stage}`);
    if (program) parts.push(`(${program})`);
    return parts.join(" ");
  }
  if (type === "Modify Tool" || type === "modify_tools_content") {
    const parts = ["Modify"];
    if (tool) parts.push(`"${tool}"`);
    if (mods) parts.push(`(${mods})`);
    if (stage) parts.push(`in ${stage}`);
    if (program) parts.push(`(${program})`);
    return parts.join(" ");
  }
  // Generic fallback
  const parts = [type || "Change"];
  if (tool) parts.push(`"${tool}"`);
  if (stage) parts.push(`in ${stage}`);
  if (program) parts.push(`(${program})`);
  return parts.join(" ");
};

// Individual sub-task row with truncate/expand
const SubTaskRow = ({ subAction, rowIndex }) => {
  const [expanded, setExpanded] = useState(false);
  const description = getSubActionDescription(subAction);
  const needsTruncation = description.length > PREVIEW_CHAR_LIMIT;
  const displayText = needsTruncation && !expanded
    ? description.slice(0, PREVIEW_CHAR_LIMIT).trimEnd() + "…"
    : description;

  return (
    <Flex key={rowIndex} align="flex-start" gap={1.5} py="2px">
      <Box w="4px" h="4px" borderRadius="full" bg="gray.300" flexShrink={0} mt="5px" />
      <Box flex={1} minW={0}>
        <Text fontSize="xs" color="gray.500" lineHeight="1.5" display="inline">
          {displayText}
        </Text>
        {needsTruncation && (
          <Text
            as="button"
            fontSize="xs"
            color="gray.400"
            _hover={{ color: "gray.600" }}
            ml={1}
            onClick={() => setExpanded(prev => !prev)}
            display="inline"
            cursor="pointer"
          >
            {expanded ? "Show less" : "Show more"}
          </Text>
        )}
      </Box>
    </Flex>
  );
};

const AIActionHandler = ({
  actions = [],
  onActionExecute,
  onActionCancel,
  isExecuting = false,
  executingActionId = null,
}) => {
  const [executedActions, setExecutedActions] = useState(new Set());
  const [actionResults, setActionResults] = useState(new Map());

  if (!actions || actions.length === 0) return null;

  const handleExecute = async (action, index) => {
    if (!onActionExecute) return;
    const actionKey = `${index}-${action.capabilityId}`;

    // Clear previous result on retry
    setActionResults(prev => { const m = new Map(prev); m.delete(actionKey); return m; });
    setExecutedActions(prev => { const s = new Set(prev); s.delete(actionKey); return s; });

    try {
      const result = await onActionExecute(action, index);
      setActionResults(prev => new Map(prev).set(actionKey, { ...result, timestamp: new Date().toISOString() }));
      if (result?.success) {
        setExecutedActions(prev => new Set(prev).add(actionKey));
      }
    } catch (err) {
      setActionResults(prev => new Map(prev).set(actionKey, {
        success: false, message: err.message || "Execution failed", error: true,
      }));
    }
  };

  const handleCancel = (action, index) => {
    if (onActionCancel) onActionCancel(action, index);
  };

  // For bulk actions, show a summary of what's changing
  const getBulkSummary = (action) => {
    if (action.capabilityId !== "bulk_actions") return null;
    const subActions = action.data?.actions || action.data?.bulkActions || [];
    if (subActions.length === 0) return null;
    return `${subActions.length} change${subActions.length !== 1 ? "s" : ""}`;
  };

  // Collect sub-actions for bulk previews from either preview.actions or data.actions.
  // Also extracts real modification content (description field) so getSubActionDescription
  // can render actual text rather than synthesized field-name summaries.
  const getBulkSubActions = (action) => {
    if (action.capabilityId !== "bulk_actions") return [];
    if (action.preview?.actions?.length > 0) {
      // preview.actions are already shaped — enrich with description if missing
      return action.preview.actions.map((pa, i) => {
        if (pa.description) return pa;
        // For Modify Tool previews the raw action may hold modification values
        const raw = (action.data?.actions || action.data?.bulkActions || [])[i];
        const mods = raw?.data?.modifications;
        if (mods && typeof mods === "object") {
          const firstVal = Object.values(mods).find(v => typeof v === "string" && v.length > 0);
          if (firstVal) return { ...pa, description: firstVal };
        }
        return pa;
      });
    }
    const raw = action.data?.actions || action.data?.bulkActions || [];
    return raw.map(a => {
      const mods = a.data?.modifications;
      // Use first non-empty modification value as the canonical description
      const modsDescription = mods && typeof mods === "object"
        ? Object.values(mods).find(v => typeof v === "string" && v.length > 0) || ""
        : "";
      return {
        type: a.type || a.capability || a.action || "",
        tool: a.data?.tool || a.data?.toolName || a.data?.toolIdentifier || "",
        stage: a.data?.stage || "",
        program: "",
        tools: a.data?.tools?.map(t => t.tool || t.name || t) || [],
        modifications: Object.keys(mods || {}).join(", "),
        newDuration: a.data?.newDuration || a.data?.duration || null,
        description: a.description || modsDescription || "",
        doneLabel: a.data?.doneLabel || "",
      };
    });
  };

  return (
    <Box width="100%" mt={2}>
      {actions.map((action, index) => {
        const actionKey = `${index}-${action.capabilityId}`;
        const result = actionResults.get(actionKey);
        const isExecuted = executedActions.has(actionKey);
        const isCurrentlyExecuting = isExecuting && executingActionId === actionKey;

        // After successful execution — show nothing (agent confirmation takes over in chat)
        if (isExecuted && result?.success) return null;

        return (
          <Box
            key={index}
            borderTop="1px solid"
            borderColor={result?.error ? "red.200" : "gray.100"}
            pt={2}
            mt={1}
          >
            {/* Error state */}
            {result && !result.success && (
              <Text fontSize="xs" color={result.error ? "red.500" : "orange.500"} mb={1}>
                {result.error ? "Failed:" : "Issue:"} {result.message}
              </Text>
            )}

            <Flex align="center" justify="flex-end" gap={2}>
              {/* Action buttons — agent's chat text already describes the changes */}
              <HStack spacing={1} flexShrink={0}>
                <Button
                  size="xs"
                  variant="ghost"
                  color="gray.400"
                  _hover={{ color: "gray.600" }}
                  onClick={() => handleCancel(action, index)}
                  isDisabled={isExecuting}
                  px={2}
                >
                  Dismiss
                </Button>
                <Button
                  size="xs"
                  colorScheme={result?.error ? "red" : "brand"}
                  variant="solid"
                  onClick={() => handleExecute(action, index)}
                  isLoading={isCurrentlyExecuting}
                  loadingText="Applying..."
                  isDisabled={isExecuting && !isCurrentlyExecuting}
                  px={3}
                >
                  {result?.error ? "Retry" : result && !result.success ? "Retry" : "Apply →"}
                </Button>
              </HStack>
            </Flex>
          </Box>
        );
      })}
    </Box>
  );
};

export default AIActionHandler;
